import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StatusProvider } from "@/context/StatusContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import RMM from "@/pages/RMM";
import Tickets from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import AssetDetail from "@/pages/AssetDetail";
import Inventory from "@/pages/Inventory";
import Network from "@/pages/Network";
import Compliance from "@/pages/Compliance";
import Security from "@/pages/Security";
import Vulnerabilities from "@/pages/Vulnerabilities";
import Docs from "@/pages/Docs";
import Vault from "@/pages/Vault";
import Contracts from "@/pages/Contracts";
import Licenses from "@/pages/Licenses";
import Backups from "@/pages/Backups";
import Reports from "@/pages/Reports";
import Business from "@/pages/Business";
import AIOps from "@/pages/AIOps";
import Agents from "@/pages/Agents";
import AgentDetail from "@/pages/AgentDetail";
import Remediation from "@/pages/Remediation";
import ClientPortal from "@/pages/ClientPortal";
import PortalTickets from "@/pages/PortalTickets";
import PortalKnowledge from "@/pages/PortalKnowledge";
import GlobalSearch from "@/pages/GlobalSearch";
import Identity from "@/pages/Identity";
import Reconciliation from "@/pages/Reconciliation";
import Evidence from "@/pages/Evidence";
import Exports from "@/pages/Exports";
import EmailEngine from "@/pages/EmailEngine";
import MFA from "@/pages/MFA";
import RecurringV2 from "@/pages/RecurringV2";
import ExecSecurity from "@/pages/ExecSecurity";
import ExecCompliance from "@/pages/ExecCompliance";
import Installer from "@/pages/Installer";
import InstallPublic from "@/pages/InstallPublic";
import DemoData from "@/pages/DemoData";
import Software from "@/pages/Software";
import Devices from "@/pages/Devices";
import Users from "@/pages/Users";
import Alerts from "@/pages/Alerts";
import Automation from "@/pages/Automation";
import ServiceDesk from "@/pages/ServiceDesk";
import TimeApproval from "@/pages/TimeApproval";
import Knowledge from "@/pages/Knowledge";
import ArticleDetail from "@/pages/ArticleDetail";
import Problems from "@/pages/Problems";
import ProblemDetail from "@/pages/ProblemDetail";
import CsatReport from "@/pages/CsatReport";
import TicketFieldSettings from "@/pages/TicketFieldSettings";
import ApprovalRuleSettings from "@/pages/ApprovalRuleSettings";
import BillingRuns from "@/pages/BillingRuns";
import ContractCharges from "@/pages/ContractCharges";
import UnbilledWork from "@/pages/UnbilledWork";

// Mirrors backend/authz.py's CLIENT_SCOPED_ROLES (also copied, for the same
// reason, in PortalTickets.jsx and NewTicketDialog.jsx). COPY ONLY -- this
// gates what RENDERS, never what the API answers; every route a client-
// scoped role could still reach by URL is refused server-side regardless
// (authz.client_scope_guard's PORTAL_ALLOW), which is what makes it safe for
// this list to be wrong in the permissive direction.
const CLIENT_SCOPED_ROLES = [
  "client_read_only", "client_admin", "client_administrator",
  "client_manager", "client_user", "client_contact",
];

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#09090B] text-zinc-400 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // A client-portal account has exactly one place to be: the door it walked
  // through by typing a bookmarked staff URL, using the browser's back
  // button, or landing here straight after login (Login.jsx sends it to
  // /portal directly; this is the fail-safe for every OTHER way in) must not
  // hand it the full MSP console shell -- every API call there 403s, which
  // rendered as a dead, zeroed-out dashboard rather than the portal that
  // already exists for this role.
  if (CLIENT_SCOPED_ROLES.includes(user.role) && !loc.pathname.startsWith("/portal")) {
    return <Navigate to="/portal" replace />;
  }
  // Key the boundary by path so a crash on one page is isolated and navigating
  // to another route mounts a fresh (uncrashed) subtree.
  return <Layout><ErrorBoundary key={loc.pathname}>{children}</ErrorBoundary></Layout>;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/install" element={<InstallPublic />} />
      <Route path="/register" element={<Register />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/rmm" element={<Protected><RMM /></Protected>} />
      <Route path="/recurring" element={<Navigate to="/recurring-v2" replace />} />
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/tickets/:id" element={<Protected><TicketDetail /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
      <Route path="/assets" element={<Navigate to="/devices" replace />} />
      <Route path="/assets/:id" element={<Protected><AssetDetail /></Protected>} />
      <Route path="/inventory" element={<Protected><Inventory /></Protected>} />
      <Route path="/network" element={<Protected><Network /></Protected>} />
      <Route path="/compliance" element={<Protected><Compliance /></Protected>} />
      <Route path="/security" element={<Protected><Security /></Protected>} />
      <Route path="/vulnerabilities" element={<Protected><Vulnerabilities /></Protected>} />
      <Route path="/docs" element={<Protected><Docs /></Protected>} />
      <Route path="/vault" element={<Protected><Vault /></Protected>} />
      <Route path="/contracts" element={<Protected><Contracts /></Protected>} />
      <Route path="/licenses" element={<Protected><Licenses /></Protected>} />
      <Route path="/backups" element={<Protected><Backups /></Protected>} />
      <Route path="/reports" element={<Protected><Reports /></Protected>} />
      <Route path="/customer-success" element={<Navigate to="/msp-bi" replace />} />
      <Route path="/msp-bi" element={<Protected><Business /></Protected>} />
      <Route path="/ai" element={<Protected><AIOps /></Protected>} />
      <Route path="/agents" element={<Protected><Agents /></Protected>} />
      <Route path="/agents/:id" element={<Protected><AgentDetail /></Protected>} />
      <Route path="/remediation" element={<Protected><Remediation /></Protected>} />
      <Route path="/portal" element={<Protected><ClientPortal /></Protected>} />
      {/* Sub-project I, Task 10: the client portal ticket list and one
          ticket's detail. Two paths, one page component -- the detail is
          the same file behind a :id, so a bookmarked link to a ticket
          that has since been merged still lands somewhere that explains
          itself rather than on the list with no note of what happened. */}
      <Route path="/portal/tickets" element={<Protected><PortalTickets /></Protected>} />
      <Route path="/portal/tickets/:id" element={<Protected><PortalTickets /></Protected>} />
      <Route path="/portal/knowledge" element={<Protected><PortalKnowledge /></Protected>} />
      <Route path="/portal/knowledge/:id" element={<Protected><PortalKnowledge /></Protected>} />
      <Route path="/search" element={<Protected><GlobalSearch /></Protected>} />
      <Route path="/identity" element={<Protected><Identity /></Protected>} />
      <Route path="/reconciliation" element={<Protected><Reconciliation /></Protected>} />
      <Route path="/evidence" element={<Protected><Evidence /></Protected>} />
      <Route path="/exports" element={<Protected><Exports /></Protected>} />
      <Route path="/email" element={<Protected><EmailEngine /></Protected>} />
      <Route path="/mfa" element={<Protected><MFA /></Protected>} />
      <Route path="/customer-risk" element={<Navigate to="/msp-bi" replace />} />
      <Route path="/leakage" element={<Navigate to="/msp-bi" replace />} />
      <Route path="/recurring-v2" element={<Protected><RecurringV2 /></Protected>} />
      <Route path="/storytelling" element={<Navigate to="/" replace />} />
      <Route path="/opportunities" element={<Navigate to="/msp-bi" replace />} />
      <Route path="/exec/security" element={<Protected><ExecSecurity /></Protected>} />
      <Route path="/exec/compliance" element={<Protected><ExecCompliance /></Protected>} />
      <Route path="/installer" element={<Protected><Installer /></Protected>} />
      <Route path="/demo" element={<Protected><DemoData /></Protected>} />
      <Route path="/software" element={<Protected><Software /></Protected>} />
      <Route path="/devices" element={<Protected><Devices /></Protected>} />
      <Route path="/users" element={<Protected><Users /></Protected>} />
      <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/automation" element={<Protected><Automation /></Protected>} />
      <Route path="/service-desk" element={<Protected><ServiceDesk /></Protected>} />
      <Route path="/time-approval" element={<Protected><TimeApproval /></Protected>} />
      <Route path="/knowledge" element={<Protected><Knowledge /></Protected>} />
      <Route path="/knowledge/:id" element={<Protected><ArticleDetail /></Protected>} />
      <Route path="/problems" element={<Protected><Problems /></Protected>} />
      <Route path="/problems/:id" element={<Protected><ProblemDetail /></Protected>} />
      <Route path="/csat" element={<Protected><CsatReport /></Protected>} />
      <Route path="/settings/ticket-fields" element={<Protected><TicketFieldSettings /></Protected>} />
      <Route path="/settings/ticket-approvals" element={<Protected><ApprovalRuleSettings /></Protected>} />
      <Route path="/billing/runs" element={<Protected><BillingRuns /></Protected>} />
      <Route path="/billing/runs/:runId" element={<Protected><BillingRuns /></Protected>} />
      <Route path="/billing/charges" element={<Protected><ContractCharges /></Protected>} />
      <Route path="/billing/unbilled" element={<Protected><UnbilledWork /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StatusProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#0a0a0c", border: "1px solid #27272A", color: "#fafafa" } }} />
        </StatusProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
